import React from 'react';
import { Container, Row, Col, Card, Alert, Table } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import PublicSiteNavbar from '../../components/PublicSiteNavbar';
import PublicSiteFooter from '../../components/PublicSiteFooter';
import NonIndexableHelmet from '../../components/NonIndexableHelmet';
import { getLegalMeta } from '../../data/nonIndexableMeta';
import { useLocale } from '../../contexts/LocaleContext';
import { getDateLocale } from '../../utils/localePaths';
import { COMPANY, HOSTING } from '../../data/legalCompanyInfo';

const RgpdPage = () => {
  const { locale } = useLocale();
  const dateLoc = getDateLocale(locale);
  return (
    <div className="min-vh-100 bg-light legal-page">
      <NonIndexableHelmet {...getLegalMeta('/legal/rgpd')} />
      <PublicSiteNavbar />

      {/* Content */}
      <Container className="py-5">
        <Row className="justify-content-center">
          <Col lg={10} xl={8}>
            <Card className="shadow-sm border-0">
              <Card.Body className="p-3 p-md-4 p-lg-5">
                <h1 className="fw-bold mb-4">
                  <i className="bi bi-shield-check text-primary me-2"></i>
                  Protection des Données - RGPD
                </h1>
                <p className="text-muted mb-4">
                  Dernière mise à jour : {new Date().toLocaleDateString(dateLoc)}
                </p>

                <Alert variant="success" className="border-0">
                  <i className="bi bi-check-circle-fill me-2"></i>
                  <strong>Engagement RGPD</strong>
                  <p className="mb-0 mt-2 small">
                    LEAGORA est pleinement engagée dans la protection de vos données personnelles et
                    respecte scrupuleusement le Règlement Général sur la Protection des Données (RGPD)
                    entré en vigueur le 25 mai 2018.
                  </p>
                </Alert>

                <hr className="my-4" />

                <section className="mb-5">
                  <h2 className="h4 fw-bold mb-3">1. Responsable du traitement</h2>
                  <p>
                    Le responsable du traitement de vos données personnelles est :
                  </p>
                  <Card className="bg-light border-0 p-3">
                    <ul className="list-unstyled mb-0">
                      <li><strong>Société :</strong> {COMPANY.legalName}</li>
                      <li><strong>Siège social :</strong> {COMPANY.address}</li>
                      <li><strong>SIREN :</strong> {COMPANY.siren}</li>
                      <li><strong>SIRET :</strong> {COMPANY.siret}</li>
                      <li><strong>RCS :</strong> {COMPANY.rcs}</li>
                      <li><strong>N° TVA intracommunautaire :</strong> {COMPANY.vat}</li>
                      <li><strong>Email :</strong> <a href={`mailto:${COMPANY.email}`}>{COMPANY.email}</a></li>
                      <li><strong>Délégué à la Protection des Données (DPO) :</strong> {COMPANY.email}</li>
                    </ul>
                  </Card>
                </section>

                <section className="mb-5">
                  <h2 className="h4 fw-bold mb-3">2. Données personnelles collectées</h2>
                  <p>
                    Dans le cadre de l'utilisation du service Assistance Video, nous collectons et
                    traitons les catégories de données personnelles suivantes :
                  </p>

                  <Table bordered hover responsive className="mt-3">
                    <thead className="table-light">
                      <tr>
                        <th>Catégorie</th>
                        <th>Données collectées</th>
                        <th>Caractère</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td><strong>Identification</strong></td>
                        <td>Email, nom, prénom, entreprise</td>
                        <td>Email obligatoire, reste optionnel</td>
                      </tr>
                      <tr>
                        <td><strong>Connexion</strong></td>
                        <td>Adresse IP, date/heure, navigateur, tokens</td>
                        <td>Automatique</td>
                      </tr>
                      <tr>
                        <td><strong>Utilisation</strong></td>
                        <td>Sessions, liens créés, crédits, statistiques</td>
                        <td>Automatique</td>
                      </tr>
                      <tr>
                        <td><strong>Paiement</strong></td>
                        <td>Historique transactions, montants</td>
                        <td>Lors d'achat uniquement</td>
                      </tr>
                      <tr>
                        <td><strong>Vidéo</strong></td>
                        <td>Flux vidéo/audio, métadonnées sessions</td>
                        <td>Non enregistré par défaut</td>
                      </tr>
                    </tbody>
                  </Table>

                  <Alert variant="info" className="border-0 mt-3">
                    <i className="bi bi-info-circle me-2"></i>
                    <strong>Note importante :</strong> Les données bancaires (numéro de carte, etc.) sont
                    collectées et traitées exclusivement par notre prestataire Stripe. LEAGORA n'a jamais
                    accès à ces données sensibles.
                  </Alert>
                </section>

                <section className="mb-5">
                  <h2 className="h4 fw-bold mb-3">3. Base légale et finalités du traitement</h2>
                  <p>
                    Conformément à l'article 6 du RGPD, le traitement de vos données repose sur les
                    bases légales suivantes :
                  </p>

                  <Table bordered hover responsive className="mt-3">
                    <thead className="table-light">
                      <tr>
                        <th>Base légale</th>
                        <th>Finalités</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td><strong>Exécution du contrat</strong><br/>(Art. 6.1.b RGPD)</td>
                        <td>
                          • Création et gestion du compte<br/>
                          • Fourniture du service de visioconférence<br/>
                          • Gestion des sessions vidéo<br/>
                          • Traitement des commandes
                        </td>
                      </tr>
                      <tr>
                        <td><strong>Consentement</strong><br/>(Art. 6.1.a RGPD)</td>
                        <td>
                          • Acceptation des CGU/CGV<br/>
                          • Enregistrement des sessions (si activé)<br/>
                          • Communications marketing (si accepté)
                        </td>
                      </tr>
                      <tr>
                        <td><strong>Intérêt légitime</strong><br/>(Art. 6.1.f RGPD)</td>
                        <td>
                          • Amélioration du service<br/>
                          • Sécurité et prévention de la fraude<br/>
                          • Statistiques anonymisées<br/>
                          • Support client
                        </td>
                      </tr>
                      <tr>
                        <td><strong>Obligation légale</strong><br/>(Art. 6.1.c RGPD)</td>
                        <td>
                          • Conservation des factures (10 ans)<br/>
                          • Obligations comptables et fiscales<br/>
                          • Réponse aux réquisitions judiciaires
                        </td>
                      </tr>
                    </tbody>
                  </Table>
                </section>

                <section className="mb-5">
                  <h2 className="h4 fw-bold mb-3">4. Destinataires des données</h2>
                  <p>
                    Vos données personnelles sont accessibles aux catégories de destinataires suivantes :
                  </p>

                  <h3 className="h6 fw-semibold mt-4 mb-2">4.1. Personnel autorisé de LEAGORA</h3>
                  <ul>
                    <li>Équipe technique (maintenance, développement)</li>
                    <li>Service client et support</li>
                    <li>Service comptabilité et facturation</li>
                  </ul>
                  <p className="small text-muted">
                    Accès limité au strict nécessaire selon le principe du moindre privilège.
                  </p>

                  <h3 className="h6 fw-semibold mt-4 mb-2">4.2. Sous-traitants et prestataires</h3>
                  <ul>
                    <li><strong>{HOSTING.name} :</strong> Hébergement applicatif (France)</li>
                    <li><strong>Stripe :</strong> Traitement des paiements (États-Unis, certifié PCI-DSS)</li>
                    <li><strong>Galene :</strong> Plateforme de visioconférence</li>
                    <li><strong>Mailjet/SMTP :</strong> Envoi des emails transactionnels</li>
                  </ul>
                  <p className="small text-muted">
                    Tous nos sous-traitants sont liés par des clauses de confidentialité strictes et
                    respectent le RGPD.
                  </p>

                  <h3 className="h6 fw-semibold mt-4 mb-2">4.3. Autorités compétentes</h3>
                  <p>
                    Sur demande légale ou judiciaire uniquement (réquisition, commission rogatoire, etc.).
                  </p>
                </section>

                <section className="mb-5">
                  <h2 className="h4 fw-bold mb-3">5. Transferts hors Union Européenne</h2>
                  <Alert variant="warning" className="border-0">
                    <i className="bi bi-globe me-2"></i>
                    <strong>Transferts internationaux</strong>
                  </Alert>
                  <p>
                    L'hébergement principal du service Assistance Video est assuré en France par {HOSTING.name}.
                    Certains prestataires (notamment Stripe pour le paiement) peuvent toutefois traiter
                    certaines données en dehors de l'Union Européenne.
                  </p>
                  <p>
                    Conformément à l'article 46 du RGPD, nous nous assurons que ces transferts bénéficient
                    de garanties appropriées :
                  </p>
                  <ul>
                    <li><strong>Clauses Contractuelles Types (CCT)</strong> de la Commission Européenne</li>
                    <li><strong>Certifications</strong> (ISO 27001, PCI-DSS, etc.)</li>
                    <li><strong>Mesures de sécurité renforcées</strong> (chiffrement, pseudonymisation)</li>
                  </ul>
                </section>

                <section className="mb-5">
                  <h2 className="h4 fw-bold mb-3">6. Durée de conservation</h2>
                  <p>
                    Vos données sont conservées pendant les durées suivantes, conformément aux
                    recommandations de la CNIL :
                  </p>

                  <Table bordered hover responsive className="mt-3">
                    <thead className="table-light">
                      <tr>
                        <th>Type de données</th>
                        <th>Durée de conservation</th>
                        <th>Base légale</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>Compte utilisateur actif</td>
                        <td>Durée d'utilisation du service</td>
                        <td>Exécution du contrat</td>
                      </tr>
                      <tr>
                        <td>Compte inactif</td>
                        <td>3 ans après dernière connexion</td>
                        <td>Intérêt légitime</td>
                      </tr>
                      <tr>
                        <td>Historique des sessions</td>
                        <td>3 ans après la session</td>
                        <td>Intérêt légitime</td>
                      </tr>
                      <tr>
                        <td>Données de facturation</td>
                        <td>10 ans</td>
                        <td>Obligation légale</td>
                      </tr>
                      <tr>
                        <td>Logs de connexion</td>
                        <td>1 an maximum</td>
                        <td>Sécurité</td>
                      </tr>
                      <tr>
                        <td>Flux vidéo/audio</td>
                        <td>Non conservés (temps réel)</td>
                        <td>N/A</td>
                      </tr>
                      <tr>
                        <td>Enregistrements vidéo (si activé)</td>
                        <td>Selon choix utilisateur</td>
                        <td>Consentement</td>
                      </tr>
                    </tbody>
                  </Table>

                  <p className="mt-3 small text-muted">
                    À l'issue de ces durées, vos données sont supprimées ou anonymisées de manière
                    irréversible, sauf obligation légale de conservation plus longue.
                  </p>
                </section>

                <section className="mb-5">
                  <h2 className="h4 fw-bold mb-3">7. Vos droits RGPD</h2>
                  <p>
                    Conformément aux articles 15 à 22 du RGPD, vous disposez des droits suivants :
                  </p>

                  <div className="row g-3 mt-3">
                    <div className="col-md-6">
                      <Card className="h-100 border-primary">
                        <Card.Body>
                          <h5 className="fw-bold text-primary">
                            <i className="bi bi-eye me-2"></i>Droit d'accès
                          </h5>
                          <p className="small mb-0">
                            Obtenir une copie de vos données personnelles et des informations sur leur
                            traitement (Art. 15 RGPD)
                          </p>
                        </Card.Body>
                      </Card>
                    </div>

                    <div className="col-md-6">
                      <Card className="h-100 border-primary">
                        <Card.Body>
                          <h5 className="fw-bold text-primary">
                            <i className="bi bi-pencil me-2"></i>Droit de rectification
                          </h5>
                          <p className="small mb-0">
                            Corriger vos données inexactes ou incomplètes (Art. 16 RGPD)
                          </p>
                        </Card.Body>
                      </Card>
                    </div>

                    <div className="col-md-6">
                      <Card className="h-100 border-primary">
                        <Card.Body>
                          <h5 className="fw-bold text-primary">
                            <i className="bi bi-trash me-2"></i>Droit à l'effacement
                          </h5>
                          <p className="small mb-0">
                            Demander la suppression de vos données (« droit à l'oubli ») sous certaines
                            conditions (Art. 17 RGPD)
                          </p>
                        </Card.Body>
                      </Card>
                    </div>

                    <div className="col-md-6">
                      <Card className="h-100 border-primary">
                        <Card.Body>
                          <h5 className="fw-bold text-primary">
                            <i className="bi bi-pause-circle me-2"></i>Droit à la limitation
                          </h5>
                          <p className="small mb-0">
                            Limiter temporairement le traitement de vos données (Art. 18 RGPD)
                          </p>
                        </Card.Body>
                      </Card>
                    </div>

                    <div className="col-md-6">
                      <Card className="h-100 border-primary">
                        <Card.Body>
                          <h5 className="fw-bold text-primary">
                            <i className="bi bi-box-arrow-right me-2"></i>Droit à la portabilité
                          </h5>
                          <p className="small mb-0">
                            Recevoir vos données dans un format structuré et les transmettre à un autre
                            responsable (Art. 20 RGPD)
                          </p>
                        </Card.Body>
                      </Card>
                    </div>

                    <div className="col-md-6">
                      <Card className="h-100 border-primary">
                        <Card.Body>
                          <h5 className="fw-bold text-primary">
                            <i className="bi bi-hand-stop me-2"></i>Droit d'opposition
                          </h5>
                          <p className="small mb-0">
                            Vous opposer au traitement de vos données pour des raisons tenant à votre
                            situation particulière (Art. 21 RGPD)
                          </p>
                        </Card.Body>
                      </Card>
                    </div>

                    <div className="col-md-6">
                      <Card className="h-100 border-primary">
                        <Card.Body>
                          <h5 className="fw-bold text-primary">
                            <i className="bi bi-x-circle me-2"></i>Retrait du consentement
                          </h5>
                          <p className="small mb-0">
                            Retirer votre consentement à tout moment (Art. 7.3 RGPD)
                          </p>
                        </Card.Body>
                      </Card>
                    </div>

                    <div className="col-md-6">
                      <Card className="h-100 border-primary">
                        <Card.Body>
                          <h5 className="fw-bold text-primary">
                            <i className="bi bi-robot me-2"></i>Décisions automatisées
                          </h5>
                          <p className="small mb-0">
                            Ne pas faire l'objet d'une décision fondée exclusivement sur un traitement
                            automatisé (Art. 22 RGPD)
                          </p>
                        </Card.Body>
                      </Card>
                    </div>
                  </div>
                </section>

                <section className="mb-5">
                  <h2 className="h4 fw-bold mb-3">8. Comment exercer vos droits ?</h2>
                  
                  <Alert variant="primary" className="border-0">
                    <h5 className="fw-bold">
                      <i className="bi bi-envelope me-2"></i>Contact
                    </h5>
                    <p>
                      Pour exercer vos droits, vous pouvez nous contacter :
                    </p>
                    <ul className="mb-0">
                      <li><strong>Par email :</strong> <a href={`mailto:${COMPANY.email}`} className="text-white text-decoration-underline">{COMPANY.email}</a></li>
                      <li><strong>Par courrier :</strong> {COMPANY.legalName} - DPO - {COMPANY.address}</li>
                    </ul>
                  </Alert>

                  <h3 className="h6 fw-semibold mt-4 mb-2">Informations à fournir</h3>
                  <p>Pour traiter votre demande, veuillez préciser :</p>
                  <ul>
                    <li>Vos nom, prénom et adresse email associée au compte</li>
                    <li>Le droit que vous souhaitez exercer</li>
                    <li>Une copie de votre pièce d'identité (pour vérification)</li>
                  </ul>

                  <h3 className="h6 fw-semibold mt-4 mb-2">Délai de réponse</h3>
                  <p>
                    Conformément à l'article 12.3 du RGPD, nous nous engageons à répondre à votre demande
                    dans un délai d'<strong>un mois</strong> maximum à compter de sa réception. Ce délai
                    peut être prolongé de deux mois en cas de complexité, avec information préalable.
                  </p>

                  <h3 className="h6 fw-semibold mt-4 mb-2">Gratuité</h3>
                  <p>
                    L'exercice de vos droits est <strong>gratuit</strong>. Toutefois, en cas de demandes
                    manifestement infondées ou excessives, nous pourrions facturer des frais raisonnables
                    ou refuser de donner suite (Art. 12.5 RGPD).
                  </p>
                </section>

                <section className="mb-5">
                  <h2 className="h4 fw-bold mb-3">9. Sécurité des données</h2>
                  <p>
                    Conformément à l'article 32 du RGPD, LEAGORA met en œuvre les mesures techniques et
                    organisationnelles appropriées pour garantir un niveau de sécurité adapté au risque :
                  </p>

                  <div className="row g-3 mt-3">
                    <div className="col-md-6">
                      <Card className="bg-light border-0 h-100">
                        <Card.Body>
                          <h6 className="fw-bold">
                            <i className="bi bi-lock-fill text-success me-2"></i>
                            Mesures techniques
                          </h6>
                          <ul className="small mb-0">
                            <li>Chiffrement HTTPS/TLS</li>
                            <li>Chiffrement de bout en bout (vidéo)</li>
                            <li>Authentification sécurisée</li>
                            <li>Pare-feu et anti-DDoS</li>
                            <li>Sauvegardes régulières</li>
                            <li>Tests de sécurité</li>
                          </ul>
                        </Card.Body>
                      </Card>
                    </div>

                    <div className="col-md-6">
                      <Card className="bg-light border-0 h-100">
                        <Card.Body>
                          <h6 className="fw-bold">
                            <i className="bi bi-people-fill text-success me-2"></i>
                            Mesures organisationnelles
                          </h6>
                          <ul className="small mb-0">
                            <li>Accès restreints (moindre privilège)</li>
                            <li>Clauses de confidentialité</li>
                            <li>Formation du personnel</li>
                            <li>Procédures de gestion des incidents</li>
                            <li>Audits réguliers</li>
                            <li>Registre des traitements</li>
                          </ul>
                        </Card.Body>
                      </Card>
                    </div>
                  </div>
                </section>

                <section className="mb-5">
                  <h2 className="h4 fw-bold mb-3">10. Violation de données</h2>
                  <p>
                    Conformément à l'article 33 du RGPD, en cas de violation de données personnelles
                    susceptible d'engendrer un risque élevé pour vos droits et libertés, nous nous
                    engageons à :
                  </p>
                  <ul>
                    <li>Notifier la CNIL dans les <strong>72 heures</strong></li>
                    <li>Vous informer directement sans délai si le risque est élevé</li>
                    <li>Décrire la nature de la violation et les mesures prises</li>
                    <li>Mettre en œuvre les mesures correctives nécessaires</li>
                  </ul>
                </section>

                <section className="mb-5">
                  <h2 className="h4 fw-bold mb-3">11. Réclamation auprès de la CNIL</h2>
                  <Alert variant="warning" className="border-0">
                    <i className="bi bi-exclamation-triangle me-2"></i>
                    <strong>Votre droit de réclamation</strong>
                  </Alert>
                  <p>
                    Si vous estimez que vos droits ne sont pas respectés ou que le traitement de vos
                    données n'est pas conforme au RGPD, vous avez le droit d'introduire une réclamation
                    auprès de l'autorité de contrôle compétente :
                  </p>

                  <Card className="bg-light border-0 p-3">
                    <h6 className="fw-bold mb-3">
                      Commission Nationale de l'Informatique et des Libertés (CNIL)
                    </h6>
                    <ul className="list-unstyled mb-0">
                      <li><strong>Adresse :</strong> 3 Place de Fontenoy - TSA 80715, 75334 PARIS CEDEX 07</li>
                      <li><strong>Téléphone :</strong> 01 53 73 22 22</li>
                      <li><strong>Site web :</strong> <a href="https://www.cnil.fr" target="_blank" rel="noopener noreferrer">www.cnil.fr</a></li>
                      <li><strong>Formulaire en ligne :</strong> <a href="https://www.cnil.fr/fr/plaintes" target="_blank" rel="noopener noreferrer">www.cnil.fr/fr/plaintes</a></li>
                    </ul>
                  </Card>

                  <p className="mt-3 small text-muted">
                    Nous vous encourageons toutefois à nous contacter en priorité afin que nous puissions
                    répondre à vos préoccupations avant toute réclamation auprès de la CNIL.
                  </p>
                </section>

                <section className="mb-5">
                  <h2 className="h4 fw-bold mb-3">12. Mises à jour</h2>
                  <p>
                    Cette page peut être mise à jour pour refléter les évolutions de nos pratiques ou
                    de la réglementation. La date de dernière mise à jour est indiquée en haut de cette page.
                  </p>
                  <p>
                    En cas de modification substantielle, nous vous en informerons par email ou via une
                    notification sur le service.
                  </p>
                </section>

                <section className="mb-5">
                  <h2 className="h4 fw-bold mb-3">13. Pour en savoir plus</h2>
                  <p>Pour plus d'informations sur la protection de vos données :</p>
                  <ul>
                    <li>
                      <Link to="/legal/privacy/">Politique de Confidentialité complète</Link>
                    </li>
                    <li>
                      <Link to="/legal/cgu/">Conditions Générales d'Utilisation</Link>
                    </li>
                    <li>
                      <a href="https://www.cnil.fr/fr/reglement-europeen-protection-donnees" target="_blank" rel="noopener noreferrer">
                        Texte du RGPD sur le site de la CNIL
                      </a>
                    </li>
                  </ul>
                </section>
              </Card.Body>
            </Card>

            {/* Navigation vers autres pages légales */}
            <div className="mt-4 text-center">
              <p className="text-muted small mb-2">Voir aussi :</p>
              <div className="d-flex flex-wrap justify-content-center gap-3">
                <Link to="/legal/mentions/" className="text-decoration-none">Mentions légales</Link>
                <span className="text-muted">•</span>
                <Link to="/legal/cgu/" className="text-decoration-none">CGU</Link>
                <span className="text-muted">•</span>
                <Link to="/legal/cgv/" className="text-decoration-none">CGV</Link>
                <span className="text-muted">•</span>
                <Link to="/legal/privacy/" className="text-decoration-none">Confidentialité</Link>
              </div>
            </div>
          </Col>
        </Row>
      </Container>

      <PublicSiteFooter />
    </div>
  );
};

export default RgpdPage;

